import { BookingPayloadModel } from "./interface/booking/bookingInterface";
import apiRequest from "../../api/apiRequest";
import { startLoader, stopLoader } from "../../util/loader";
import { isTestEnvironment, isValidZipCode, storeFailedResponse, removeNullAndEmpty, normalizeKeys, enviromentCheck, convertBookingToContactPayload } from "./helperModules/helperFunctions";
import { timeBlockInfoKeys, regionalTaxKeys, taxRatesKeys, taxAuthoritiesKeys, hmsRoleDetailKeys, propertyDetailsKeys, packagePayloadKeys, servicePayloadKeys, bookingPayloadModelKeys, leadAdditionalNotesKeys, imageUrlsKeys, hmsServiceBundleKeys, hmsAvailableServiceKeys, jobPromiseEndTimeKeys, hmsRoleInfosKeys, crossSellServicesKeys, crossSellDetailsKeys } from "./keys/bookingKeys";
import { handleFakeSubmit, getCookie, normalizeUSZipCode } from "../../util/share";
import { handleMissingData } from "../../util/discovery";
import { successLogger } from "../../api/apiTester";
import { apiConfig } from "../../config/apiConfig";
import { contactUsPostCallWrapper}  from "./apiContactCallWrapper";
import { fireGTMEvent } from "../../util/gtmEventHandlers";
import { REGEX } from "../../constants/regex";
import { conceptIdLookupForNblyOrCorp } from "../../nbly-brand/helper_functions";
import { parseAddressRobust } from "./helperModules/handleBookingSubmit";

interface VendorDetails {
  vendorName: string;
  vendorId: number;
}
 
// Move to config file or environment
const NBLY_VENDOR_CONFIG: VendorDetails = {
  vendorName: 'Neighborly Website',
  vendorId: Number(process.env.NBLY_VENDOR_ID) || 48,
};

const lapCodes = {
  code_lap: 'LAP',
  id_lap: '29',
  replace_name: process.env.REPLACE_LAST_NAME_BOOKING === 'true'
}

const rpmCodes = {
  code_rpm: 'RPM',
  id_rpm: 17,
}

//Check if the payload is valid
function isValidBookingPayload(payload: any): payload is BookingPayloadModel {
    // checking if all mandatory fields are present
    const mandatoryFields = ['webLocationId', 'postalCode', 'city', 'state', 'address', 'conceptCalledId', 'isLeadOnly', 'callType', 'campaignName', 'leadOrigin', 'firstName', 'lastName'];
    const hasAllMandatoryFields = mandatoryFields.every(field => payload.hasOwnProperty(field));
    if (!hasAllMandatoryFields) {
      console.log("Missing mandatory fields in the payload:", payload);
      return false;
    }

    //checking if any invalid keys are present
    if (!validateNestedObject(payload, bookingPayloadModelKeys)){
      console.log("Invalid keys or dataTypes detected in the payload:", payload);
      return false;
    }

    //check for valid Zipcode and updated trimmed zipcode
    if (!isValidZipCode(payload.zipCode)) return false;

    payload.zipCode = isValidZipCode(payload.zipCode);

    return payload;
}

//Prepopulating all the required field which come in via a web lead
async function prePopulatePayload(payload:any){
  payload.callType = payload.callType ?? 'In Bound';
  payload.campaignName = payload.campaignName ?? 'Web';
  payload.leadOrigin = payload.leadOrigin ?? 'BrandWebsite';
  payload.lastName = payload.lastName ?? ' ';
  //check for the enviroment and send in the payload
  payload.isTest = isTestEnvironment();
  const leadAdditionalNoteLabel = utmCampaign();
  if(leadAdditionalNoteLabel){
    payload.leadAdditionalNotes = payload.leadAdditionalNotes ?? {}
    if (payload.leadAdditionalNotes.leadAdditionalNoteLabel) {
        payload.leadAdditionalNotes.leadAdditionalNoteLabel = `${leadAdditionalNoteLabel}`;
    } else {
      payload.leadAdditionalNotes.leadAdditionalNoteLabel = leadAdditionalNoteLabel;
    }
  }

  // Only parse if address exists and is not a placeholder
  const addressLower = payload.address?.toLowerCase()?.trim() || "";
  const isPlaceholder = !addressLower || addressLower === "no address collected" || addressLower === "no address";
  
 
  

  const city =  payload.City ?? payload.city;
  const state = payload.State ?? payload.state;
  if(!REGEX.sendCaPostalCode.test(payload.zipCode) && payload?.zipCode?.includes('-')) {
       payload.zipCode = normalizeUSZipCode(payload.zipCode);
  }
  const out = await handleMissingData(city, state, payload.ZipCode ?? payload.zipCode);
  payload.city = out?.city || city;
  payload.state = out?.state || state;
  payload.address = payload.address.replace(/^,/, '').trim();

  //Append URL to notes if the flag is set in brand config
  const brandData = JSON.parse(localStorage.getItem("brandDetails") || '{}');
  if(brandData.append_url_to_notes){
    const urlText = `URL: ${sessionStorage.getItem("campaignName") ?? window.location.href}`;
    if (!payload.note?.includes(urlText)) {
      payload.note = `${payload.note} | ${urlText}`;
    }
  }
   
  if(!isPlaceholder){
    const addressObj = { 
      city: payload.city,
      state: payload.state,
      country: payload.country,
      zipcode: payload.zipCode,
      us_originalName: "us",
      us_shortened: "USA",
      us_fullname: "United States of America",
      us_altName: "United States",
      ca_originalName: "ca",
      ca_shortened: "CAN",
      ca_fullname: "Canada",
      ca_altName: "Canada",
      state_code: sessionStorage.getItem("manualStateCode") || ""
    }
    const result = parseAddressRobust(payload.address, addressObj);
    payload.address = result || "No Address Collected";
  }
  
  //Tech Story 480733: [OPUS 2.0] Remove the param "Comments" & "Preferred Method" from booking end point in OPUS 2.0
 
  if((payload.conceptCode)?.toUpperCase() !== rpmCodes.code_rpm){
    delete payload.comments;
  }
 
  delete payload.preferredCommunicationType;
  //- 
  if(conceptIdLookupForNblyOrCorp()){
    payload = replaceVendorDetailsForNBLY(payload);
  }
  return payload;
}


/**
 * Story: 605179 [OPUS 2.0][NBLY] : Always Send VendorName as “Neighborly Website” for NEI2 Domains & Vendor ID as 48
 * Replaces vendor details in booking payload for NBLY/Corp brands
 * @param payload - Booking payload to modify
 * @returns New payload object with updated vendor details
 */

function replaceVendorDetailsForNBLY(payload: BookingPayloadModel): BookingPayloadModel {
  if (!payload) {
    console.error('Invalid payload provided to replaceVendorDetailsForNBLY');
    return payload;
  }
 
  // Return new object instead of mutating
  return {
    ...payload,
    vendorName: NBLY_VENDOR_CONFIG.vendorName,
    vendorId: NBLY_VENDOR_CONFIG.vendorId,
  };
}

/**
 * Common function to extract UTM parameter values from a URLSearchParams object
 * @param searchParams - URLSearchParams object to extract values from
 * @returns Object with Marketing-prefixed keys and their values
 */
export function extractUtmParams(searchParams: URLSearchParams): { [key: string]: string } {
  const params = [
    "campaign",
    "source",
    "medium",
    "term",
    "content",
    "device",
    "aff_id"
  ];

  return params.reduce((obj, key) => {
    // Special case: aff_id doesn't use utm_ prefix
    const paramName = key === "aff_id" ? "aff_id" : `utm_${key}`;
    const value = searchParams.get(paramName);
    if (value) {
      // Convert aff_id to MarketingAff_id, others to standard format
      const marketingKey = key === "aff_id" 
        ? "aff_id" 
        : `Marketing${key.charAt(0).toUpperCase() + key.slice(1)}`;
      obj[marketingKey] = value;
    }
    return obj;
  }, {} as { [key: string]: string });
}

/**
 * Formats UTM parameters object into a readable string
 * @param utmParams - Object with UTM parameter key-value pairs
 * @returns Formatted string with key-value pairs joined by commas
 */
export function formatUtmParamsToString(utmParams: { [key: string]: string }): string {
  return Object.entries(utmParams)
    .filter(([key, value]) => {
      // Detect and filter out template injection patterns
      if (/\{\{.*?\}\}|\$\{.*?\}/.test(value)) {
        console.warn(`Template injection detected and removed from ${key}: ${value}`);
        return false; // Exclude this parameter
      }
      return true; // Keep valid parameters
    })
    .map(([key, value]) => `${key}: ${value}`)
    .join(', ');
}

export function utmCampaign(): string {
  const url = new URLSearchParams(window.location.search);
  const utmParams = extractUtmParams(url);
  
  if (Object.keys(utmParams).length > 0) {
    return formatUtmParamsToString(utmParams);
  }

  // Fallback to session storage if no UTM params in URL
  const campaign = sessionStorage.getItem("campaignName");
  if (campaign) {
    try {
      const queryString = campaign.includes('?') ? campaign.split('?')[1] : campaign;
      const campaignParams = new URLSearchParams(queryString);
      const sessionUtmParams = extractUtmParams(campaignParams);
      
      if (Object.keys(sessionUtmParams).length > 0) {
        return formatUtmParamsToString(sessionUtmParams);
      }
    } catch (error) {
      console.warn('Error parsing campaign data from sessionStorage:', error);
    }
  }
  return '';
}

//Key Validator starts
function validateObjectKeys(obj: any, validKeys: { [key: string]: string }): boolean {
  return Object.keys(obj).every(key => {
    if (!validKeys.hasOwnProperty(key)) {
      return false
    };
    const expectedType = validKeys[key];
    const actualType = Array.isArray(obj[key]) ? 'object' : typeof obj[key];
    if(actualType !== expectedType)
      console.log(`The ${key} has ${actualType} but the expected type is ${expectedType}`)
    return actualType === expectedType;
  });
}

function validateNestedObject(obj: any, keyMap: { [key: string]: string }): boolean {
  if (!validateObjectKeys(obj, keyMap)) {
    console.log("Main Root Level payload validation failed")
    return false
  };

  if (obj.hmsRoleInfo && !validateHmsRoleInfo(obj.hmsRoleInfo)) {
    console.log("HMSInfo payload validation failed")
    return false
  };
  if (obj.timeBlock && !validateObjectKeys(obj.timeBlock, timeBlockInfoKeys)) {
    console.log("TimeBlock payload validation failed")
    return false
  };
  if (obj.hmsRoleInfo?.hmsSelectedServiceDetail?.hmsServiceBundles && !validateServiceBundles(obj.hmsRoleInfo.hmsSelectedServiceDetail.hmsServiceBundles)) {
    console.log("HmsServiceBundles payload validation failed")
    return false
  };
  if (obj.leadAdditionalNotes && !validateAdditionalNotes(obj.leadAdditionalNotes)){
    console.log("LeadAdditionalNotes payload validation failed")
    return false
  };
  if (obj.imageUrls && !validateImageUrls(obj.imageUrls)){
    console.log("ImageUrls payload validation failed")
    return false
  };
  if (obj.jobPromiseEndTime && !validateObjectKeys(obj.jobPromiseEndTime, jobPromiseEndTimeKeys)){
    console.log("JobPromiseEndTime payload validation failed")
    return false
  };
  if (obj.crossSellDetails && !validateCrossSellDetails(obj.crossSellDetails)){
    console.log("CrossSellDetails payload validation failed")
    return false
  };

  return true;
}

function validateCrossSellDetails(crossSellDetails: any[]): boolean {
  if (!Array.isArray(crossSellDetails)) {
    console.log("crossSellDetails is not an array")
    return false
  };
  for (const detail of crossSellDetails) {
    if (!validateObjectKeys(detail, crossSellDetailsKeys)) {
      console.log("crossSellDetailsKeys payload validation failed")
      return false
    };
    if (!detail.conceptCode) {
      console.log("conceptCode is required in crossSellDetails")
      return false
    };
    if (!detail.services) {
      console.log("services array is required in crossSellDetails")
      return false
    };
    if (!validateCrossSellServices(detail.services)) {
      console.log("crossSellServices payload validation failed")
      return false
    };
  }
  return true;
}

function validateCrossSellServices(services: any[]): boolean {
  if (!Array.isArray(services)) {
    console.log("services is not an array")
    return false
  };
  for (const service of services) {
    if (!validateObjectKeys(service, crossSellServicesKeys)) {
      console.log("crossSellServicesKeys payload validation failed")
      return false
    };
    if (!service.hasOwnProperty('serviceId')) {
      console.log("serviceId is required in services")
      return false
    };
    if (!service.hasOwnProperty('serviceSkuId')) {
      console.log("serviceSkuId is required in services")
      return false
    };
    if (!service.hasOwnProperty('serviceName')) {
      console.log("serviceName is required in services")
      return false
    };
  }
  return true;
}

function validateHmsRoleInfo(hmsRoleInfo: any): boolean {
  if (!validateObjectKeys(hmsRoleInfo, hmsRoleInfosKeys)){
    console.log("hmsRoleInfosKeys payload validation failed")
    return false
  };
  if (hmsRoleInfo.hmsRoleDetail && !validateObjectKeys(hmsRoleInfo.hmsRoleDetail, hmsRoleDetailKeys)){
    console.log("hmsRoleDetail payload validation failed")
    return false
  };
  if (hmsRoleInfo.hmsPropertyDetail && !validateObjectKeys(hmsRoleInfo.hmsPropertyDetail, propertyDetailsKeys)) {
    console.log("hmsPropertyDetail payload validation failed")
    return false
  };
  if (hmsRoleInfo.hmsSelectedServiceDetail && !validateSelectedServiceDetail(hmsRoleInfo.hmsSelectedServiceDetail)) {
    console.log("hmsSelectedServiceDetail payload validation failed")
    return false
  };
  if (hmsRoleInfo.hmsTaxDetail && !validateTaxDetail(hmsRoleInfo.hmsTaxDetail)) {
    console.log("hmsTaxDetail payload validation failed")
    return false
  };

  return true;
}

function validateSelectedServiceDetail(selectedServiceDetail: any): boolean {
  if (!validateObjectKeys(selectedServiceDetail, servicePayloadKeys)) {
    console.log("servicePayloadKeys payload validation failed")
    return false
  };
  if (selectedServiceDetail.hmsAvailableServices && !validateAvailableServices(selectedServiceDetail.hmsAvailableServices)) {
    console.log("hmsAvailableServices payload validation failed")
    return false
  };
  if (selectedServiceDetail.hmsServiceBundles && !validateServiceBundles(selectedServiceDetail.hmsServiceBundles)) {
    console.log("hmsServiceBundles payload validation failed")
    return false
  };

  return true;
}

function validateAvailableServices(availableServices: any[]): boolean {
  for (const service of availableServices) {
    if (!validateObjectKeys(service, hmsAvailableServiceKeys)) {
      console.log("hmsAvailableServiceKeys payload validation failed")
      return false
    };
  }
  return true;
}

function validateServiceBundles(serviceBundles: any[]): boolean {
  for (const bundle of serviceBundles) {
    if (!validateObjectKeys(bundle, hmsServiceBundleKeys)) {
      console.log("hmsServiceBundleKeys payload validation failed")
      return false
    };
    if (bundle.packagePayload && !validatePackagePayload(bundle.packagePayload)) {
      console.log("serviceBundles payload validation failed")
      return false
    };  
  }
  return true;
}

function validatePackagePayload(packagePayload: any[]): boolean {
  for (const payload of packagePayload) {
    if (!validateObjectKeys(payload, packagePayloadKeys)) {
      console.log("packagePayload payload validation failed")
      return false
    };  
  }
  return true;
}

function validateTaxDetail(taxDetail: any): boolean {
  if (!validateObjectKeys(taxDetail, bookingPayloadModelKeys)) {
    console.log("taxDetail payload validation failed")
    return false
  };
  if (taxDetail.regionalTaxes && !validateRegionalTaxes(taxDetail.regionalTaxes)) {
    console.log("regionalTaxes payload validation failed")
    return false
  }; 

  return true;
}

function validateRegionalTaxes(regionalTaxes: any[]): boolean {
  for (const tax of regionalTaxes) {
    if (!validateObjectKeys(tax, regionalTaxKeys)) {
      console.log("tax payload validation failed")
      return false
    }; 
    if (tax.taxRate && !validateObjectKeys(tax.taxRate, taxRatesKeys)) {
      console.log("taxRate payload validation failed")
      return false
    }; 
    if (tax.taxAuthority && !validateObjectKeys(tax.taxAuthority, taxAuthoritiesKeys)) {
      console.log("taxAuthority payload validation failed")
      return false
    };
  }
  return true;
}

function validateAdditionalNotes(additionalNote: any[]): boolean {
  if (typeof additionalNote !== 'object') {
    console.log("Invalid additionalNote: not an object");
    return false;
  }

  if (!validateObjectKeys(additionalNote, leadAdditionalNotesKeys)) {
    console.log("leadAdditionalNotesKeys payload validation failed");
    return false;
  }
  return true;
}

function validateImageUrls(imageUrls: any[]): boolean {
  for (const url of imageUrls) {
    if (!validateObjectKeys(url, imageUrlsKeys)) {
      console.log("imageUrlsKeys payload validation failed")
      return false
    };
  }
  return true;
}

function findAndModifyForLAP(payload: BookingPayloadModel): BookingPayloadModel {
  const conceptId = (document.getElementById("conceptId") as HTMLInputElement)?.value ?? payload?.conceptId;
  if(conceptId !== lapCodes.id_lap)
    return payload;
  if(lapCodes.replace_name){
    payload.lastName = 'Test';
  }
  return payload;
}


//Key Validator ends

//Calling the booking end point
export async function bookingUsPostCallWrapper(url: string, payload: any, isShortForm = false) {
  const suLogger = localStorage.getItem("suLogger") === 'true';
  // const fallbackMechanismFlag = localStorage.getItem("fallbackCall") === 'true';
  const fallbackMechanismFlag = false;

  // Set gaValue from cookie before prepopulating payload
  payload.gaValue = getCookie('_ga');

  //returns true if pass and false if fail
  if(!enviromentCheck(url)){
    stopLoader();
    return false;
  }

  let formattedPayload = await prePopulatePayload(payload);
  
  const enableStrictValidation = process.env.ENABLE_STRICT_PAYLOAD_VALIDATION?.toLowerCase() === 'true';
  if(enableStrictValidation){
    formattedPayload = JSON.parse(JSON.stringify(normalizeKeys(formattedPayload)));
    formattedPayload = JSON.parse(JSON.stringify(removeNullAndEmpty(formattedPayload)));
    formattedPayload = await prePopulatePayload(formattedPayload);

    if (!isValidBookingPayload(formattedPayload)) {
      console.error('Invalid payload for Booking Endpoint:', formattedPayload);
      throw new Error('Invalid payload for Booking Endpoint:' + JSON.stringify(formattedPayload));
    }
    else{
      formattedPayload = isValidBookingPayload(formattedPayload);
    }
  }
  else{
    console.log("Strict Payload validation disabled") 
  }

  const request = {
    method: 'POST',
    url,
    data: findAndModifyForLAP(formattedPayload),
  };
  //Code to handle Fake Submit
  if (request?.data?.firstName?.toLowerCase().includes("isfakesubmit") || request?.data?.lastName?.toLowerCase().includes("isfakesubmit")) {
    handleFakeSubmit(request).then(() => {
      console.log("Fake Submit Request triggered");
    }).catch((error) => {
      console.error("Error in Fake Submit:", error);
    }).finally(() => {
      return Promise.resolve(true);
    });
    const formdata = JSON.stringify(payload);
    sessionStorage.setItem('fakeSubmitData',formdata);
    return true;
  }
  try {
    startLoader();
    const response = await apiRequest(request);
    successLogger(request.data, request.url, suLogger, response);
    fireGTMEvent('success', request.data, isShortForm);
    if(response)
      sessionStorage.setItem('bookingResponse', JSON.stringify(response));
    return response;
  } catch (err) {
    fireGTMEvent('fail', request.data, isShortForm);
    console.error('Error:', err);
    storeFailedResponse(request, err);
    if(fallbackMechanismFlag)
      fallbackMechanism(request.data);
    throw(err);
  } finally {
    stopLoader();
    sessionStorage.removeItem('isRequestCallBackChecked');
  }
}

function fallbackMechanism(payload: any) {
  const contactPayload = convertBookingToContactPayload(payload);
    if(contactPayload){
      contactUsPostCallWrapper(apiConfig.CONTACT_US_SEND, contactPayload).then((response) => {
        console.log("Contact Response", response);
      }).catch((error) => {
        console.error("Error in Contact Response:", error);
      })
    }
}